import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import {
  Users,
  Folder,
  FileText,
  Mail,
  CheckSquare,
  Calendar,
  CreditCard,
  MessageSquare,
  ChevronRight,
  UserPlus,
  Upload,
  Send,
  CheckCircle2,
  Inbox,
  Clock,
} from 'lucide-react';
import './DashboardPage.css';

const getDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const sameLocalDay = (dateA, dateB) => {
  if (!dateA || !dateB) return false;
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const formatRelativeTime = (date) => {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 20) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 14) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const normalizeStatusLabel = (status) => {
  if (!status) return '';
  return String(status).replace(/_/g, ' ');
};

/**
 * Dashboard page.
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, tenant } = useEntitlements();

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const displayName = user?.first_name || user?.email || 'there';
  const tenantName = tenant?.firm_name || tenant?.name || 'Portalr';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const unwrapResults = (settled) => {
      if (settled.status !== 'fulfilled') return [];
      const data = settled.value?.data;
      if (!data) return [];
      return data.results || data || [];
    };

    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [clientsRes, docsRes, messagesRes, convRes, tasksRes, invoicesRes, appointmentsRes] =
          await Promise.allSettled([
            api.get('/v1/clients/'),
            api.get('/v1/documents/'),
            api.get('/v1/messages/'),
            api.get('/v1/chat/conversations/'),
            api.get('/v1/tasks/'),
            api.get('/v1/invoices/'),
            api.get('/v1/appointments/'),
          ]);

        if (cancelled) return;
        setClients(unwrapResults(clientsRes));
        setDocuments(unwrapResults(docsRes));
        setMessages(unwrapResults(messagesRes));
        setConversations(unwrapResults(convRes));
        setTasks(unwrapResults(tasksRes));
        setInvoices(unwrapResults(invoicesRes));
        setAppointments(unwrapResults(appointmentsRes));
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const topLevelMessages = useMemo(() => {
    return messages.filter((m) => !m.parent_message);
  }, [messages]);

  const unreadMessages = useMemo(() => {
    return topLevelMessages
      .filter((m) => m.status && m.status !== 'read')
      .sort((a, b) => {
        const da = getDateTime(a.created_at)?.getTime() || 0;
        const db = getDateTime(b.created_at)?.getTime() || 0;
        return db - da;
      });
  }, [topLevelMessages]);

  const clientsStats = useMemo(() => {
    const total = clients.length;
    const active = clients.filter((c) => c.is_active !== false).length;
    return { total, active };
  }, [clients]);

  const documentsStats = useMemo(() => {
    const total = documents.length;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recent = documents.filter((d) => {
      const dt = getDateTime(d.created_at);
      return dt && dt >= sevenDaysAgo;
    }).length;
    return { total, recent };
  }, [documents]);

  const messagesStats = useMemo(() => {
    const unread = unreadMessages.length;
    const convCount = conversations.length;
    return { unread, convCount };
  }, [unreadMessages.length, conversations.length]);

  const tasksStats = useMemo(() => {
    const pending = tasks.filter((t) => !['completed', 'cancelled'].includes(t.status)).length;
    const now = new Date();
    const dueToday = tasks.filter((t) => {
      if (!t.due_date) return false;
      if (['completed', 'cancelled'].includes(t.status)) return false;
      const due = getDateTime(t.due_date);
      return sameLocalDay(due, now);
    }).length;
    return { pending, dueToday };
  }, [tasks]);

  const quickActions = useMemo(() => {
    const base = [
      {
        key: 'tasks',
        icon: CheckSquare,
        title: 'View Tasks',
        desc: 'Track what needs attention.',
        onClick: () => navigate('/tasks'),
      },
      {
        key: 'appointments',
        icon: Calendar,
        title: 'View Appointments',
        desc: 'Your schedule at a glance.',
        onClick: () => navigate('/appointments'),
      },
      {
        key: 'invoices',
        icon: CreditCard,
        title: 'View Invoices',
        desc: 'Billing status & payment actions.',
        onClick: () => navigate('/invoices'),
      },
      {
        key: 'documents',
        icon: Folder,
        title: 'Documents',
        desc: 'Files, uploads, and sharing.',
        onClick: () => navigate('/documents'),
      },
      {
        key: 'messages',
        icon: MessageSquare,
        title: 'Messages',
        desc: 'In-app threads and chat.',
        onClick: () => navigate('/messages'),
      },
    ];

    const staff = [
      {
        key: 'clients',
        icon: Users,
        title: 'Manage Clients',
        desc: 'Add, activate, and administer.',
        onClick: () => navigate('/clients'),
      },
      {
        key: 'subscriptions',
        icon: FileText,
        title: 'View Subscriptions',
        desc: 'Plan and module status.',
        onClick: () => navigate('/settings/subscriptions'),
      },
    ];

    const admin = [
      {
        key: 'team',
        icon: Users,
        title: 'Team Management',
        desc: 'Roles, access, and invitations.',
        onClick: () => navigate('/settings/team'),
      },
    ];

    return [...base, ...(isStaff ? staff : []), ...(isAdmin ? admin : [])];
  }, [isAdmin, isStaff, navigate]);

  const recentActivity = useMemo(() => {
    const events = [];

    const now = new Date();
    const pushEvent = (time, badgeVariant, title, meta, accentDot = false, id) => {
      const t = getDateTime(time)?.getTime();
      if (!t) return;
      events.push({
        id: id || `${title}-${t}`,
        time: t,
        badgeVariant,
        title,
        meta,
        accentDot,
      });
    };

    const sortedClients = [...clients].sort((a, b) => (getDateTime(b.created_at)?.getTime() || 0) - (getDateTime(a.created_at)?.getTime() || 0));
    sortedClients.slice(0, 2).forEach((c, idx) => {
      pushEvent(
        c.created_at,
        '',
        `New client added`,
        `${c.name || c.company_name || 'Client'} • ${formatRelativeTime(getDateTime(c.created_at))}`,
        false,
        `client-${idx}`
      );
    });

    const sortedDocs = [...documents].sort((a, b) => (getDateTime(b.created_at)?.getTime() || 0) - (getDateTime(a.created_at)?.getTime() || 0));
    sortedDocs.slice(0, 2).forEach((d, idx) => {
      pushEvent(
        d.created_at,
        '',
        `${d.document_type ? normalizeStatusLabel(d.document_type) : 'Document'} uploaded`,
        `${d.name || 'File'} • ${formatRelativeTime(getDateTime(d.created_at))}`,
        false,
        `doc-${idx}`
      );
    });

    const sortedMessages = [...topLevelMessages].sort((a, b) => (getDateTime(b.created_at)?.getTime() || 0) - (getDateTime(a.created_at)?.getTime() || 0));
    sortedMessages.slice(0, 3).forEach((m, idx) => {
      const isUnread = m.status && m.status !== 'read';
      pushEvent(
        m.created_at,
        isUnread ? 'accent' : '',
        `Message received`,
        `${m.subject || 'New message'} • ${formatRelativeTime(getDateTime(m.created_at))}`,
        isUnread,
        `msg-${idx}`
      );
    });

    const sentInvoices = [...invoices]
      .filter((i) => i.status === 'sent')
      .sort((a, b) => (getDateTime(b.issue_date)?.getTime() || 0) - (getDateTime(a.issue_date)?.getTime() || 0));
    sentInvoices.slice(0, 1).forEach((inv, idx) => {
      pushEvent(
        inv.issue_date || inv.created_at,
        'warning',
        `Invoice sent`,
        `${inv.invoice_number || 'Invoice'} • ${formatRelativeTime(getDateTime(inv.issue_date || inv.created_at))}`,
        false,
        `inv-${idx}`
      );
    });

    const completedTasks = [...tasks]
      .filter((t) => t.status === 'completed')
      .sort((a, b) => (getDateTime(b.updated_at)?.getTime() || getDateTime(b.created_at)?.getTime() || 0) - (getDateTime(a.updated_at)?.getTime() || getDateTime(a.created_at)?.getTime() || 0));
    completedTasks.slice(0, 2).forEach((t, idx) => {
      pushEvent(
        t.updated_at || t.created_at,
        'success',
        `Task completed`,
        `${t.title || 'Task'} • ${formatRelativeTime(getDateTime(t.updated_at || t.created_at))}`,
        false,
        `task-${idx}`
      );
    });

    return events
      .sort((a, b) => b.time - a.time)
      .slice(0, 8)
      .map((e) => ({
        ...e,
        meta: e.meta,
        timeLabel: e.time ? formatRelativeTime(new Date(e.time)) : '',
      }));
  }, [appointments, clients, documents, invoices, tasks, topLevelMessages]);

  const recentMessagesPreview = useMemo(() => {
    if (unreadMessages.length > 0) return unreadMessages.slice(0, 4);
    const sorted = [...topLevelMessages].sort((a, b) => (getDateTime(b.created_at)?.getTime() || 0) - (getDateTime(a.created_at)?.getTime() || 0));
    return sorted.slice(0, 4);
  }, [topLevelMessages, unreadMessages]);

  const upcomingPreview = useMemo(() => {
    const now = new Date();

    const pendingTasks = tasks.filter((t) => !['completed', 'cancelled'].includes(t.status) && t.due_date);
    pendingTasks.sort((a, b) => (getDateTime(a.due_date)?.getTime() || 0) - (getDateTime(b.due_date)?.getTime() || 0));

    const upcomingTasks = pendingTasks
      .filter((t) => {
        const due = getDateTime(t.due_date);
        if (!due) return false;
        return due.getTime() >= now.getTime() - 24 * 60 * 60 * 1000; // include yesterday if still relevant
      })
      .slice(0, 3);

    if (upcomingTasks.length > 0) {
      return { mode: 'tasks', items: upcomingTasks };
    }

    const upcomingAppointments = appointments
      .filter((a) => a.start_time && !['cancelled', 'completed'].includes(a.status))
      .sort((a, b) => (getDateTime(a.start_time)?.getTime() || 0) - (getDateTime(b.start_time)?.getTime() || 0))
      .slice(0, 3);

    return { mode: 'appointments', items: upcomingAppointments };
  }, [appointments, tasks]);

  const today = useMemo(() => new Date(), []);

  return (
    <div className="dashboard-page portalr-container">
      <section className="dashboard-hero" aria-label="Dashboard intro">
        <div className="dashboard-heroGlow" aria-hidden="true" />
        <h1 className="dashboard-heroTitle">
          Welcome back, {displayName}
        </h1>
        <p className="dashboard-heroSubtitle">
          Here’s what’s happening in your portal today
          {tenant ? ` • ${tenantName}` : ''}
        </p>
      </section>

      {error && (
        <div className="dashboard-error" role="alert">
          {error}
        </div>
      )}

      <section className="dashboard-stats" aria-label="Summary">
        <div className="portalr-statGrid">
          <div className="portalr-statCard">
            <div className="portalr-statTop">
              <div>
                <p className="portalr-statLabel">Clients</p>
                <div className="portalr-statMetric">
                  {loading ? '—' : clientsStats.active}
                  {!loading && clientsStats.total !== undefined ? '' : ''}
                </div>
                <div className="portalr-statSecondary">
                  {loading ? 'Loading…' : `${clientsStats.total} total • ${clientsStats.active} active`}
                </div>
              </div>
              <div className="portalr-statIcon" aria-hidden="true">
                <Users size={18} />
              </div>
            </div>
          </div>

          <div className={`portalr-statCard ${documentsStats.recent > 0 ? 'portalr-statCard--accentGlow portalr-statCard--accentBorder' : ''}`}>
            <div className="portalr-statTop">
              <div>
                <p className="portalr-statLabel">Documents</p>
                <div className="portalr-statMetric">
                  {loading ? '—' : documentsStats.total}
                </div>
                <div className="portalr-statSecondary">
                  {loading ? 'Loading…' : `${documentsStats.recent} recent uploads`}
                </div>
              </div>
              <div className="portalr-statIcon" aria-hidden="true">
                <Folder size={18} />
              </div>
            </div>
          </div>

          <div className={`portalr-statCard ${messagesStats.unread > 0 ? 'portalr-statCard--accentGlow portalr-statCard--accentBorder' : ''}`}>
            <div className="portalr-statTop">
              <div>
                <p className="portalr-statLabel">Messages</p>
                <div className="portalr-statMetric">
                  {loading ? '—' : messagesStats.unread}
                </div>
                <div className="portalr-statSecondary">
                  {loading ? 'Loading…' : `${messagesStats.convCount} conversations`}
                </div>
              </div>
              <div className="portalr-statIcon" aria-hidden="true">
                <Mail size={18} />
              </div>
            </div>
          </div>

          <div className={`portalr-statCard ${tasksStats.dueToday > 0 ? 'portalr-statCard--accentGlow portalr-statCard--accentBorder' : ''}`}>
            <div className="portalr-statTop">
              <div>
                <p className="portalr-statLabel">Tasks</p>
                <div className="portalr-statMetric">
                  {loading ? '—' : tasksStats.pending}
                </div>
                <div className="portalr-statSecondary">
                  {loading ? 'Loading…' : `${tasksStats.dueToday} due today`}
                </div>
              </div>
              <div className="portalr-statIcon" aria-hidden="true">
                <CheckSquare size={18} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-twoCol" aria-label="Actions and previews">
        <div className="portalr-card portalr-card--elevated">
          <div className="portalr-cardInner">
            <div className="dashboard-panelHeader">
              <div>
                <h2 className="dashboard-panelTitle">Quick Actions</h2>
                <p className="dashboard-panelSubtitle">Jump to the work that matters now.</p>
              </div>
              <div className="dashboard-panelHeaderRight" />
            </div>

            <div className="portalr-actionGrid">
              {quickActions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className="portalr-actionTile dashboard-actionTile"
                  onClick={a.onClick}
                >
                  <span className="portalr-actionLeft">
                    <span className="portalr-actionIcon" aria-hidden="true">
                      <a.icon size={18} />
                    </span>
                    <span>
                      <span className="portalr-actionTitle">{a.title}</span>
                      <span className="portalr-actionDesc">{a.desc}</span>
                    </span>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="dashboard-previewStack">
          <div className="portalr-card portalr-card--elevated">
            <div className="portalr-cardInner">
              <div className="dashboard-panelHeader">
                <div>
                  <h2 className="dashboard-panelTitle">Recent Messages</h2>
                  <p className="dashboard-panelSubtitle">Unread items first.</p>
                </div>
                <button type="button" className="portalr-btn portalr-btn--secondary dashboard-miniCta" onClick={() => navigate('/messages')}>
                  Open
                </button>
              </div>

              {loading ? (
                <div className="dashboard-loadingInline">Loading…</div>
              ) : recentMessagesPreview.length === 0 ? (
                <div className="portalr-emptyState">
                  <div className="portalr-emptyStateIcon" aria-hidden="true">
                    <Inbox size={20} />
                  </div>
                  <div className="portalr-emptyStateTitle">No messages yet</div>
                  <p className="portalr-emptyStateDesc">Send your first update to keep clients moving forward.</p>
                  <div style={{ marginTop: '1rem' }}>
                    <button type="button" className="portalr-btn portalr-btn--primary" onClick={() => navigate('/messages')}>
                      Send a message
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="dashboard-miniList">
                  {recentMessagesPreview.map((m) => {
                    const dt = getDateTime(m.created_at);
                    const isUnread = m.status && m.status !== 'read';
                    return (
                      <div
                        key={m.id}
                        className={`dashboard-miniItem ${isUnread ? 'dashboard-miniItem--accent' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate('/messages')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') navigate('/messages');
                        }}
                      >
                        <div className="dashboard-miniItemTop">
                          <strong className="dashboard-miniItemTitle">
                            {m.subject || 'New message'}
                          </strong>
                          {isUnread ? <span className="portalr-badge portalr-badge--accent">Unread</span> : null}
                        </div>
                        <div className="dashboard-miniItemMeta">
                          {m.sender_name || m.sender_email || 'Unknown'} • {dt ? formatRelativeTime(dt) : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="portalr-card portalr-card--elevated">
            <div className="portalr-cardInner">
              <div className="dashboard-panelHeader">
                <div>
                  <h2 className="dashboard-panelTitle">{upcomingPreview.mode === 'tasks' ? 'Upcoming Tasks' : 'Upcoming Appointments'}</h2>
                  <p className="dashboard-panelSubtitle">Prioritized by due date.</p>
                </div>
                {upcomingPreview.mode === 'tasks' ? (
                  <button type="button" className="portalr-btn portalr-btn--secondary dashboard-miniCta" onClick={() => navigate('/tasks')}>
                    View
                  </button>
                ) : (
                  <button type="button" className="portalr-btn portalr-btn--secondary dashboard-miniCta" onClick={() => navigate('/appointments')}>
                    View
                  </button>
                )}
              </div>

              {loading ? (
                <div className="dashboard-loadingInline">Loading…</div>
              ) : upcomingPreview.items.length === 0 ? (
                <div className="portalr-emptyState">
                  <div className="portalr-emptyStateIcon" aria-hidden="true">
                    <Clock size={20} />
                  </div>
                  <div className="portalr-emptyStateTitle">You’re all caught up</div>
                  <p className="portalr-emptyStateDesc">
                    {isStaff ? 'Create the next task to keep operations moving.' : 'Schedule your next appointment when you’re ready.'}
                  </p>
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="portalr-btn portalr-btn--primary"
                      onClick={() => {
                        if (isStaff) navigate('/tasks');
                        else navigate('/appointments');
                      }}
                    >
                      {isStaff ? 'Create a task' : 'Request appointment'}
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="dashboard-miniList">
                  {upcomingPreview.items.map((item) => {
                    if (upcomingPreview.mode === 'tasks') {
                      const task = item;
                      const due = getDateTime(task.due_date);
                      const overdue = due && due.getTime() < today.getTime();
                      const dueToday = due && sameLocalDay(due, today);
                      const badgeVariant = overdue ? 'danger' : dueToday ? 'accent' : '';
                      const badge =
                        overdue ? (
                          <span className="portalr-badge portalr-badge--danger">Overdue</span>
                        ) : dueToday ? (
                          <span className="portalr-badge portalr-badge--accent">Due today</span>
                        ) : (
                          task.priority ? <span className="portalr-badge">{task.priority}</span> : null
                        );

                      return (
                        <div
                          key={task.id}
                          className={`dashboard-miniItem ${badgeVariant === 'danger' ? 'dashboard-miniItem--accent' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate('/tasks')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') navigate('/tasks');
                          }}
                        >
                          <div className="dashboard-miniItemTop">
                            <strong className="dashboard-miniItemTitle">{task.title || 'Task'}</strong>
                            {badge}
                          </div>
                          <div className="dashboard-miniItemMeta">
                            Due {due ? due.toLocaleDateString() : '—'} • {normalizeStatusLabel(task.status)}
                          </div>
                        </div>
                      );
                    }

                    const appt = item;
                    const start = getDateTime(appt.start_time);
                    const badgeVariant = appt.status === 'scheduled' ? 'success' : appt.status === 'requested' ? 'warning' : '';
                    const badgeLabel = normalizeStatusLabel(appt.status);
                    return (
                      <div
                        key={appt.id}
                        className="dashboard-miniItem"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate('/appointments')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') navigate('/appointments');
                        }}
                      >
                        <div className="dashboard-miniItemTop">
                          <strong className="dashboard-miniItemTitle">{appt.title || 'Appointment'}</strong>
                          {badgeVariant ? <span className={`portalr-badge portalr-badge--${badgeVariant}`}>{badgeLabel || 'Scheduled'}</span> : <span className="portalr-badge">{badgeLabel}</span>}
                        </div>
                        <div className="dashboard-miniItemMeta">
                          {start ? start.toLocaleString() : '—'} • {normalizeStatusLabel(appt.meeting_type || 'internal')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-activity" aria-label="Recent activity">
        <div className="portalr-card portalr-card--elevated">
          <div className="portalr-cardInner">
            <div className="dashboard-panelHeader">
              <div>
                <h2 className="dashboard-panelTitle">Recent Activity</h2>
                <p className="dashboard-panelSubtitle">A live snapshot of what changed in your portal.</p>
              </div>
              <div />
            </div>

            {loading ? (
              <div className="dashboard-loadingInline dashboard-loadingInline--center">Loading activity…</div>
            ) : recentActivity.length === 0 ? (
              <div className="portalr-emptyState">
                <div className="portalr-emptyStateIcon" aria-hidden="true">
                  <Upload size={20} />
                </div>
                <div className="portalr-emptyStateTitle">No activity to show</div>
                <p className="portalr-emptyStateDesc">
                  Start by adding a client, uploading a document, or sending a message.
                </p>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {isStaff && (
                    <button type="button" className="portalr-btn portalr-btn--secondary" onClick={() => navigate('/clients')}>
                      Add client
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  )}
                  <button type="button" className="portalr-btn portalr-btn--primary" onClick={() => navigate('/documents')}>
                    Upload document
                    <ChevronRight size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="portalr-timeline">
                {recentActivity.map((e) => (
                  <div
                    key={e.id}
                    className={`portalr-timelineItem ${e.accentDot ? 'portalr-timelineItem--accent' : ''}`}
                  >
                    <div className="portalr-timelineDot" aria-hidden="true" />
                    <div className="portalr-timelineMain">
                      <div className="portalr-timelineTitle">
                        <span>{e.title}</span>
                        {e.badgeVariant ? (
                          <span
                            className={`portalr-badge ${
                              e.badgeVariant === 'accent'
                                ? 'portalr-badge--accent'
                                : e.badgeVariant === 'success'
                                  ? 'portalr-badge--success'
                                  : e.badgeVariant === 'warning'
                                    ? 'portalr-badge--warning'
                                    : e.badgeVariant === 'danger'
                                      ? 'portalr-badge--danger'
                                      : ''
                            }`}
                          >
                            {e.badgeVariant === 'accent'
                              ? 'Important'
                              : e.badgeVariant === 'success'
                                ? 'Done'
                                : e.badgeVariant === 'warning'
                                  ? 'Billing'
                                  : e.badgeVariant === 'danger'
                                    ? 'Overdue'
                                    : e.badgeVariant}
                          </span>
                        ) : null}
                      </div>
                      <div className="portalr-timelineMeta">{e.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

